import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MapService } from '../_services/map.service';
import { AlertService } from '../_services/alert.service';
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Group,
  Vector as VectorLayer,
} from 'ol/layer';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import View from 'ol/View';
import {
  ZoomToExtent,
  Rotate,
  OverviewMap,
  ScaleLine,
  MousePosition,
  ZoomSlider,
} from 'ol/control';
import Bar from 'ol-ext/control/Bar';
import Toggle from 'ol-ext/control/Toggle';
import Button from 'ol-ext/control/Button';
import { ParentMessageService } from '../_services/parent-message.service';
import { ParentMessage } from '../_models/parent-message';
import { SessionService } from '../_services/session.service';
import { L10nTranslationService } from 'angular-l10n';
import Scale from 'ol-ext/control/Scale';
import { createStringXY } from 'ol/coordinate';
import LayerSwitcher from 'ol-ext/control/LayerSwitcher';
import { Fill, Stroke, Style } from 'ol/style';
import { Draw } from 'ol/interaction';
import { Vector as VectorSource } from 'ol/source';
import { MessageConfig } from '../_models/message-config';
import GML_PARSER from 'ol/format/GML32';
import Feature from 'ol/Feature';
import GeoJSON_PARSER from 'ol/format/GeoJSON';
import { getArea } from 'ol/sphere';
import WMTS from 'ol/source/WMTS.js';
import { get as getProjection } from 'ol/proj.js';
import WMTSTileGrid from 'ol/tilegrid/WMTS.js';
import { getTopLeft, getWidth } from 'ol/extent.js';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit {
  MAP_READY_MESSAGE_ID = 'MAP_READY_MESSAGE_ID';
  MAP_POINT_SELECTED_MESSAGE_ID = 'MAP_POINT_SELECTED_MESSAGE_ID';
  MAP_POLYGON_SELECTED_MESSAGE_ID = 'MAP_POLYGON_SELECTED_MESSAGE_ID';
  APP_ZOOM_TO_COORDINATES_MESSAGE_ID = 'APP_ZOOM_TO_COORDINATES_MESSAGE_ID';
  APP_POLYGON_SENT_MESSAGE_ID = 'APP_POLYGON_SENT_MESSAGE_ID';

  vectorSource: VectorSource;
  gmlParser: any = new GML_PARSER({
    srsName: 'http://www.opengis.net/gml/srs/epsg.xml#4326',
  });
  geoJSONParser: any = new GeoJSON_PARSER();
  showSearchCoordinates: boolean = false;
  searchCoordinatesForm: FormGroup;

  fullExtent = [14, 39.3, 19, 42.5];

  geoserverWMSBaseUrl =
    'https://geoserver.carbonagrisoil.com/Carbonagrisoil/wms';
  geoserverWMSServices = [
    'Carbonagrisoil:Confini_provinciali_PB',
    'Carbonagrisoil:RunOff_Frutteti',
    'Carbonagrisoil:RunOff_Oliveti',
    'Carbonagrisoil:RunOff_Seminativi',
    'Carbonagrisoil:RunOff_Vigneti',
    'Carbonagrisoil:Stazioni_meteo_Basilicata',
    'Carbonagrisoil:Stazioni_meteo_Puglia',
    'Carbonagrisoil:StockCarbonio_Frutteti',
    'Carbonagrisoil:StockCarbonio_Oliveti',
    'Carbonagrisoil:StockCarbonio_Seminativi',
    'Carbonagrisoil:StockCarbonio_Vigneti',
    'Carbonagrisoil:SuoloEroso_Frutteti',
    'Carbonagrisoil:SuoloEroso_Oliveti',
    'Carbonagrisoil:SuoloEroso_Seminativi',
    'Carbonagrisoil:SuoloEroso_Vigneti',
    'Carbonagrisoil:Uso_del_suolo',
  ];

  constructor(
    private mapService: MapService,
    private parentMessageService: ParentMessageService,
    private session: SessionService,
    private translation: L10nTranslationService,
    private fb: FormBuilder,
    private alert: AlertService
  ) {}

  ngOnInit(): void {
    const self = this;

    const projection = getProjection('EPSG:3857');
    const projectionExtent = projection.getExtent();
    const size = getWidth(projectionExtent) / 256;
    const resolutions = new Array(19);
    const matrixIds = new Array(19);
    for (let z = 0; z < 19; ++z) {
      // generate resolutions and matrixIds arrays for this WMTS
      resolutions[z] = size / Math.pow(2, z);
      matrixIds[z] = z;
    }

    const ortophoto = new TileLayer({
      id: 'ortofoto',
      title: this.translation.translate('map.orthophoto'),
      source: new WMTS({
        attributions:
          "Powered by <a href='https://www.esri.com/en-us/home' target='_blank'>Esri</a> | " +
          'Sources: Esri, TomTom, Garmin, FAO, NOAA, USGS, © OpenStreetMap contributors, and the GIS User Community',
        url: 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/WMTS',
        layer: 'sgmc2',
        matrixSet: 'GoogleMapsCompatible',
        format: 'image/png',
        projection: projection,
        tileGrid: new WMTSTileGrid({
          origin: getTopLeft(projectionExtent),
          resolutions: resolutions,
          matrixIds: matrixIds,
        }),
        style: 'default',
        wrapX: true,
      }),
    });

    const getAdeImageLayer = (id: string, title: string, layerName: string) => {
      return new ImageLayer({
        id,
        title,
        source: new ImageWMS({
          extent: [2, 33, 19, 48],
          url: 'https://wms.cartografia.agenziaentrate.gov.it/inspire/wms/ows01.php',
          params: { LAYERS: layerName },
          projection: 'EPSG:6706',
          imageLoadFunction: function (image, src) {
            const index = src.indexOf('?');
            const params = new URLSearchParams(src.slice(index));
            const bbox = (params.get('BBOX') || '').split(',');
            params.set('BBOX', [bbox[1], bbox[0], bbox[3], bbox[2]].toString());
            image.getImage().src = src.slice(0, index + 1) + params.toString();
          },
        }),
      });
    };

    const particelle = getAdeImageLayer(
      'particelle',
      this.translation.translate('map.particles'),
      'CP.CadastralParcel'
    );

    const codiciParticelle = getAdeImageLayer(
      'codiciParticelle',
      this.translation.translate('map.particleCodes'),
      'codice_plla'
    );

    const wmsLayers: ImageLayer[] = [];
    for (const currentWMSLayerName of this.geoserverWMSServices) {
      const titleId = 'map.wmsLayers.layers.' + currentWMSLayerName;

      const currentWMSLayer = new ImageLayer({
        id: currentWMSLayerName,
        title: this.translation.translate(titleId),
        visible: false,
        source: new ImageWMS({
          extent: this.fullExtent,
          url: this.geoserverWMSBaseUrl,
          params: { LAYERS: currentWMSLayerName },
        }),
      });

      wmsLayers.push(currentWMSLayer);
    }

    const wmsLayersGroup = new Group({ // NOSONAR
      title: this.translation.translate('map.wmsLayers.title'),
      openInLayerSwitcher: false,
      layers: wmsLayers,
      visible: false,
    });

    this.vectorSource = new VectorSource({
      projection: 'EPSG:4326',
    });
    const vettoriale = new VectorLayer({
      id: 'vettoriale',
      source: this.vectorSource,
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
          color: '#ffcc33',
          width: 2,
        }),
      }),
    });

    const layers = [
      ortophoto,
      particelle,
      codiciParticelle,
      // wmsLayersGroup,
      vettoriale,
    ];

    const view = new View({
      center: [16.5, 40.9],
      minZoom: 7,
      maxZoom: 20,
      zoom: 7,
      projection: 'EPSG:4326',
      extent: this.fullExtent,
    });

    const overviewLayers = [
      new TileLayer({
        source: new TileWMS({
          extent: [27.546328, -31.385193, 71.274744, 44.932709],
          url: 'https://copernicus.discomap.eea.europa.eu/arcgis/services/GioLand/VeryHighResolution2012/MapServer/WmsServer',
          params: { LAYERS: 'Image', TILED: true },
        }),
      }),
    ];

    const mainbar = new Bar();
    const nested = new Bar({ toggleOne: true, group: true });
    mainbar.addControl(nested);

    // draw polygon
    const drawInteraction = new Draw({
      source: this.vectorSource,
      type: 'Polygon',
    });
    drawInteraction.on('drawstart', this.resetVectorSource.bind(this));
    drawInteraction.on('drawend', (drawEvent) =>
      self.sendPolygonToApp(drawEvent.feature)
    );

    const drawPolygon = new Toggle({
      html: '<i class="fas fa-draw-polygon"></i>',
      className: 'select',
      title: this.translation.translate('map.controls.drawPolygon.tip'),
      interaction: drawInteraction,
      active: false,
      onToggle: self.resetVectorSource.bind(this),
    });
    nested.addControl(drawPolygon);

    /* mainbar control */
    mainbar.addControl(
      new ZoomToExtent({
        extent: this.fullExtent,
        tipLabel: this.translation.translate('map.controls.fitToExtent.tip'),
      })
    );
    mainbar.addControl(
      new Rotate({
        tipLabel: this.translation.translate('map.controls.rotate.tip'),
      })
    );

    const searchCoordinates = new Button({
      html: '<i class="fas fa-crosshairs"></i>',
      className: 'select',
      title: this.translation.translate('map.controls.searchCoordinates.tip'),
      interaction: drawInteraction,
      active: false,
      handleClick: function (active) {
        self.showSearchCoordinates = !self.showSearchCoordinates;
      },
    });
    mainbar.addControl(searchCoordinates);
    this.searchCoordinatesForm = this.fb.group({
      longitude: [''],
      latitude: [''],
    });

    const overviewMapControl = new OverviewMap({
      layers: overviewLayers,
      tipLabel: this.translation.translate('map.overview.tip'),
    });

    const scaleControl = new ScaleLine({
      units: 'metric',
    });

    const mousePositionControl = new MousePosition({
      coordinateFormat: createStringXY(4),
      projection: 'EPSG:4326',
      undefinedHTML: '&nbsp;',
    });

    const layerSwitcherControl = new LayerSwitcher({
      displayInLayerSwitcher: function (layer) {
        if (layer.get('id') === 'vettoriale') {
          return false;
        }

        return true;
      },
    });

    const mapControls = [
      new ZoomSlider(),
      new Scale(),
      overviewMapControl,
      scaleControl,
      mousePositionControl,
      mainbar,
      layerSwitcherControl,
    ];

    this.mapService.init(layers, 'map', view, mapControls);

    // subscribe to app messages
    let polygonSentMessageConfig = new MessageConfig(
      this.APP_POLYGON_SENT_MESSAGE_ID,
      this.receivePolygonFromApp,
      this
    );
    this.parentMessageService.register(polygonSentMessageConfig);
    let zoomToCoordinatesMessageConfig = new MessageConfig(
      this.APP_ZOOM_TO_COORDINATES_MESSAGE_ID,
      this.zoomToCoordinatesFromApp,
      this
    );
    this.parentMessageService.register(zoomToCoordinatesMessageConfig);

    // send map ready message to parent
    const parentMessage = new ParentMessage();
    parentMessage.id = this.MAP_READY_MESSAGE_ID;
    parentMessage.object = {
      ready: true,
    };
    this.parentMessageService.sendMessage(parentMessage);
  }

  sendPointCoordinatesToApp(coordinates) {
    const parentMessage = new ParentMessage();
    parentMessage.id = this.MAP_POINT_SELECTED_MESSAGE_ID;
    parentMessage.object = coordinates;

    this.parentMessageService.sendMessage(parentMessage);
  }

  receivePolygonFromApp(parentMessage: ParentMessage) {
    let geoJSONPolygon = parentMessage.object.geoJSONPolygon;
    let geometry = this.geoJSONParser.readGeometry(geoJSONPolygon);
    let feature = new Feature();
    feature.setGeometry(geometry);

    this.vectorSource.addFeature(feature);
    this.mapService.zoomToFeature(feature);
  }

  zoomToCoordinatesFromApp(parentMessage: ParentMessage) {
    let coordinates = parentMessage.object.coordinates;

    if (
      this.mapService.zoomToCoordinates(coordinates, this.fullExtent) === false
    ) {
      this.alert.warning(
        this.translation.translate(
          'map.controls.searchCoordinates.coordinatesOutOfBounds'
        )
      );
    }
  }

  sendPolygonToApp(feature: Feature) {
    let polygon = feature.getGeometry();
    let gmlPolygonNode = this.gmlParser.writeGeometryNode(polygon);
    let gmlPolygon = gmlPolygonNode.innerHTML;
    let geoJSONPolygon = this.geoJSONParser.writeGeometry(polygon);
    let polygonArea = getArea(polygon, {
      projection: 'EPSG:4326',
    });

    const parentMessage = new ParentMessage();
    parentMessage.id = this.MAP_POLYGON_SELECTED_MESSAGE_ID;
    parentMessage.object = {
      gmlPolygon: gmlPolygon,
      geoJSONPolygon: geoJSONPolygon,
      polygonArea: polygonArea,
    };

    this.parentMessageService.sendMessage(parentMessage);
  }

  resetVectorSource() {
    this.vectorSource.clear();
  }

  searchCoordinates() {
    const longitudeInput = this.searchCoordinatesForm.get('longitude');
    const latitudeInput = this.searchCoordinatesForm.get('latitude');

    if (
      !this.searchCoordinatesForm.valid ||
      longitudeInput === null ||
      latitudeInput === null
    ) {
      return;
    }

    let longitude = longitudeInput.value;
    let latitude = latitudeInput.value;

    if (
      this.mapService.zoomToCoordinates(
        [longitude, latitude],
        this.fullExtent
      ) === false
    ) {
      this.alert.warning(
        this.translation.translate(
          'map.controls.searchCoordinates.coordinatesOutOfBounds'
        )
      );
    }
  }
}
